import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listTables,
  listZones,
  updateTableStatus,
  listActiveTableReservations,
  getReservationDetail,

} from "@/lib/admin.functions";
import { adminUpdateReservationStatus } from "@/lib/reservations.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ReservationNotes } from "@/components/admin/ReservationNotes";
import {
  ReservationSourceBadge,
  bookedByName,
} from "@/components/admin/ReservationSourceBadge";
import {
  EditReservationDialog,
  type EditableReservation,
} from "@/components/admin/EditReservationDialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/floor-plan")({
  component: FloorPlan,
});

type Status = "available" | "occupied" | "reserved" | "out_of_service";
const STATUSES: Status[] = ["available", "occupied", "reserved", "out_of_service"];

const STATUS_STYLE: Record<Status, string> = {
  available: "border-emerald-500/50 bg-emerald-500/10 text-emerald-200",
  occupied: "border-rose-500/50 bg-rose-500/10 text-rose-200",
  reserved: "border-amber-500/50 bg-amber-500/10 text-amber-200",
  out_of_service: "border-zinc-500/50 bg-zinc-500/10 text-zinc-300",
};
const STATUS_DOT: Record<Status, string> = {
  available: "bg-emerald-500",
  occupied: "bg-rose-500",
  reserved: "bg-amber-500",
  out_of_service: "bg-zinc-500",
};
const STATUS_LABEL: Record<Status, string> = {
  available: "Available",
  occupied: "Occupied",
  reserved: "Reserved",
  out_of_service: "Out of service",
};

const ZONE_ORDER = [
  "Front Bar",
  "Middle",
  "Cocktail Bar",
  "Darts",
  "Upstairs",
];

const ZONE_NOTES: Record<string, string> = {
  "Front Bar":
    "Tables 1–3 can be joined together for bookings between 4 and 10 guests.",
  "Middle":
    "Tables 10–15 seat 4 each. Tables 17 & 18 seat 6 each. Large groups can book multiple tables but tables cannot be joined.",
  "Cocktail Bar":
    "Table 20 seats 4. Tables 21–25 (2 each) can be joined for 4–12 guests. Tables 31–34 seat 4 each — 31+32 can be booked as 8, 33+34 can be booked as 8.",
  "Darts": "Two dart boards available for booking.",
  "Upstairs": "Two pool tables upstairs — €12 per hour.",
};


function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Minutes since midnight in Europe/Dublin for an ISO timestamp. */
function dublinMinutes(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Dublin",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return h * 60 + m;
}

function hhmmToMinutes(v: string): number {
  const [h, m] = v.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Today's date in Dublin as YYYY-MM-DD. */
function dublinToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Dublin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Every table id a reservation occupies (supports multi-table group bookings). */
function tableIdsOf(r: { table_id: string | null; reservation_tables?: { table_id: string }[] | null }) {
  const ids = new Set<string>();
  if (r.table_id) ids.add(r.table_id);
  for (const l of r.reservation_tables ?? []) if (l?.table_id) ids.add(l.table_id);
  return Array.from(ids);
}

/** Dublin calendar day (YYYY-MM-DD) for an ISO timestamp. */
function dublinYmdOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Dublin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

function FloorPlan() {
  const tFn = useServerFn(listTables);
  const zFn = useServerFn(listZones);
  const rFn = useServerFn(listActiveTableReservations);
  const upd = useServerFn(updateTableStatus);
  const updRes = useServerFn(adminUpdateReservationStatus);
  const qc = useQueryClient();

  const [day, setDay] = useState<string>(() => dublinToday());
  const isToday = day === dublinToday();

  const { data: tables = [] } = useQuery({ queryKey: ["tables"], queryFn: () => tFn() });
  const { data: zones = [] } = useQuery({ queryKey: ["zones"], queryFn: () => zFn() });
  const { data: activeRes = [] } = useQuery({
    queryKey: ["table-reservations", day],
    queryFn: () => rFn({ data: { date: day } }),
    refetchInterval: 30_000,
  });


  useEffect(() => {
    const ch = supabase
      .channel("floor-plan-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "tables" }, () => {
        qc.invalidateQueries({ queryKey: ["tables"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "reservations" }, () => {
        qc.invalidateQueries({ queryKey: ["table-reservations"] });
        qc.invalidateQueries({ queryKey: ["tables"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const mut = useMutation({
    mutationFn: (v: { id: string; status: Status; reason?: string }) =>
      upd({ data: v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tables"] });
      toast.success("Table status updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Time-range filter (Dublin local time, HH:MM). Defaults cover the full day.
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");

  const filteredRes = useMemo(() => {
    const fromM = hhmmToMinutes(fromTime);
    const toM = hhmmToMinutes(toTime);
    return activeRes.filter((r) => {
      const startM = dublinMinutes(r.starts_at);
      const endM = startM + (r.duration_minutes ?? 0);
      // Overlap between [startM, endM] and [fromM, toM]
      return startM <= toM && endM >= fromM;
    });
  }, [activeRes, fromTime, toTime]);

  const resByTable = useMemo(() => {
    const m = new Map<string, typeof activeRes>();
    for (const r of filteredRes) {
      for (const tid of tableIdsOf(r)) {
        const arr = m.get(tid) ?? [];
        arr.push(r);
        m.set(tid, arr);
      }
    }
    return m;
  }, [filteredRes]);

  // All of the selected day's bookings per table, ignoring the time filter.
  const allByTable = useMemo(() => {
    const m = new Map<string, typeof activeRes>();
    for (const r of activeRes) {
      for (const tid of tableIdsOf(r)) {
        const arr = m.get(tid) ?? [];
        arr.push(r);
        m.set(tid, arr);
      }
    }
    return m;
  }, [activeRes]);


  /**
   * Status shown for the selected day. Manual statuses (occupied/reserved) only
   * hold for the Dublin day they were set on, so a table never carries a hold
   * over midnight into the next day. Out of service is persistent.
   */
  const effectiveStatus = useCallback(
    (
      t: { status: string; status_changed_at?: string | null },
      dayBookings: { status: string }[],
    ): Status => {
      if (t.status === "out_of_service") return "out_of_service";
      const hasSeated = dayBookings.some(
        (r) => r.status === "seated" || r.status === "checked_in",
      );
      if (hasSeated) return "occupied";
      const hasHold = dayBookings.some(
        (r) =>
          r.status === "confirmed" ||
          r.status === "pending_approval" ||
          r.status === "pending_payment",
      );
      if (hasHold) return "reserved";
      // No booking for this day: keep a manual walk-in status only if it was
      // set on the day being viewed, otherwise the table is free.
      if (dublinYmdOf(t.status_changed_at) === day) return t.status as Status;
      return "available";
    },
    [day],
  );

  const unassigned = useMemo(
    () => filteredRes.filter((r) => tableIdsOf(r).length === 0 && r.status !== "cancelled"),
    [filteredRes],
  );

  const assignMut = useMutation({
    mutationFn: (v: { id: string; table_id: string }) =>
      updRes({ data: { id: v.id, status: "confirmed", table_id: v.table_id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-reservations"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["admin-reservations"] });
      toast.success("Table assigned");
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const grouped = useMemo(() => {
    const byZone = new Map<string, { name: string; tables: typeof tables }>();
    for (const z of zones) byZone.set(z.id, { name: z.name, tables: [] });
    const orphan: typeof tables = [];
    for (const t of tables) {
      if (t.zone_id && byZone.has(t.zone_id)) byZone.get(t.zone_id)!.tables.push(t);
      else orphan.push(t);
    }
    const list = Array.from(byZone.values())
      .filter((z) => z.tables.length > 0)
      .sort(
        (a, b) =>
          (ZONE_ORDER.indexOf(a.name) === -1 ? 999 : ZONE_ORDER.indexOf(a.name)) -
          (ZONE_ORDER.indexOf(b.name) === -1 ? 999 : ZONE_ORDER.indexOf(b.name)),
      );
    if (orphan.length) list.push({ name: "Unassigned", tables: orphan });
    return list;
  }, [tables, zones]);

  const totals = useMemo(() => {
    const counts: Record<Status, number> = {
      available: 0,
      occupied: 0,
      reserved: 0,
      out_of_service: 0,
    };
    for (const t of tables) counts[effectiveStatus(t, allByTable.get(t.id) ?? [])]++;
    return counts;
  }, [tables, allByTable, effectiveStatus]);

  // Confirmation dialog state
  const [confirmState, setConfirmState] = useState<{
    tableId: string;
    label: string;
    from: Status;
    to: Status;
  } | null>(null);
  const [reason, setReason] = useState("");

  // Collapsed tables (bookings are shown by default when a table has any)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Reservation detail drill-down
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditableReservation | null>(null);
  const detailRes = useMemo(
    () => activeRes.find((r) => r.id === detailId) ?? null,
    [activeRes, detailId],
  );
  const detailTableIds = detailRes ? tableIdsOf(detailRes) : [];
  const tablesMut = useMutation({
    mutationFn: (v: { id: string; status: string; table_ids: string[] }) =>
      updRes({ data: { id: v.id, status: v.status as never, table_ids: v.table_ids } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-reservations"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["admin-reservations"] });
      toast.success("Tables updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const detailFn = useServerFn(getReservationDetail);
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["reservation-detail", detailId],
    queryFn: () => detailFn({ data: { id: detailId! } }),
    enabled: !!detailId,
  });


  const requestChange = (t: { id: string; label: string; status: string }, to: Status) => {
    if (t.status === to) return;
    setReason("");
    setConfirmState({ tableId: t.id, label: t.label, from: t.status as Status, to });
  };

  const confirmChange = () => {
    if (!confirmState) return;
    mut.mutate({
      id: confirmState.tableId,
      status: confirmState.to,
      reason: reason || undefined,
    });
    setConfirmState(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Floor plan</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live view of every table across the venue. Reservations sync automatically —
            status changes are logged.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {STATUSES.map((s) => (
            <span
              key={s}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${STATUS_STYLE[s]}`}
            >
              <span className={`h-2 w-2 rounded-full ${STATUS_DOT[s]}`} />
              {STATUS_LABEL[s]} · {totals[s]}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/30 p-3">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Day</label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous day"
              onClick={() => setDay((d) => addDays(d, -1))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm hover:bg-muted/40"
            >
              ‹
            </button>
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value || dublinToday())}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
            <button
              type="button"
              aria-label="Next day"
              onClick={() => setDay((d) => addDays(d, 1))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm hover:bg-muted/40"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setDay(dublinToday())}
              disabled={isToday}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40 disabled:opacity-50"
            >
              Today
            </button>
          </div>
        </div>
        <div className="flex flex-col">

          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</label>
          <input
            type="time"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</label>
          <input
            type="time"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: "All day", from: "00:00", to: "23:59" },
            { label: "Lunch", from: "12:00", to: "15:00" },
            { label: "Afternoon", from: "15:00", to: "18:00" },
            { label: "Evening", from: "18:00", to: "22:00" },
            { label: "Late", from: "22:00", to: "23:59" },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setFromTime(p.from);
                setToTime(p.to);
              }}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40"
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          Showing {filteredRes.length} of {activeRes.length} bookings on{" "}
          {isToday ? "today" : new Date(`${day}T12:00:00Z`).toLocaleDateString("en-IE", { weekday: "short", day: "2-digit", month: "short" })}{" "}
          ({fromTime}–{toTime})

        </span>
      </div>

      {unassigned.length > 0 && (
        <section className="space-y-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-xl text-amber-200">
              {isToday ? "Today's" : "Selected day's"} bookings without a table
            </h2>
            <span className="text-xs text-muted-foreground">{unassigned.length}</span>
          </div>
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {unassigned.map((r) => {
              const guest = Array.isArray(r.guests) ? r.guests[0] : r.guests;
              return (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card/40 p-3"
                >
                  <button
                    type="button"
                    onClick={() => setDetailId(r.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="text-sm font-medium">
                      {fmtTime(r.starts_at)} · {guest?.full_name ?? "Guest"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      party {r.party_size} · {r.status.replace("_", " ")} · Ref {r.reference}
                    </div>
                  </button>
                  <select
                    defaultValue=""
                    disabled={assignMut.isPending}
                    onChange={(e) => {
                      if (e.target.value) assignMut.mutate({ id: r.id, table_id: e.target.value });
                    }}
                    className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                  >
                    <option value="">Assign table…</option>
                    {tables.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label} (cap {t.capacity})
                      </option>
                    ))}
                  </select>
                </li>
              );
            })}
          </ul>
        </section>
      )}


      {grouped.map((zone) => {
        const zoneNote = ZONE_NOTES[zone.name];
        return (
        <section key={zone.name} className="space-y-3">
          <div className="flex items-baseline justify-between border-b border-border/60 pb-2">
            <div>
              <h2 className="font-display text-xl">{zone.name}</h2>
              {zoneNote && (
                <p className="mt-1 max-w-3xl text-xs text-muted-foreground">{zoneNote}</p>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {zone.tables.length} table{zone.tables.length === 1 ? "" : "s"}
            </span>
          </div>


          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {zone.tables.map((t) => {
              const reservations = resByTable.get(t.id) ?? [];
              const dayBookings = allByTable.get(t.id) ?? [];
              const dayCount = dayBookings.length;
              const status = effectiveStatus(t, dayBookings);
              const isOpen = reservations.length > 0 && !collapsed[t.id];
              return (
                <div
                  key={t.id}
                  className={`rounded-2xl border p-4 transition ${STATUS_STYLE[status]}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsed((prev) => ({ ...prev, [t.id]: !prev[t.id] }))
                    }
                    className="flex w-full items-start justify-between text-left"
                    aria-expanded={isOpen}
                  >
                    <div>
                      <div className="font-display text-lg leading-tight">{t.label}</div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        cap {t.capacity} · {t.resource_type.replace(/_/g, " ")} ·{" "}
                        {reservations.length} booking{reservations.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full bg-black/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status]}`} />
                      {STATUS_LABEL[status]}
                    </span>
                  </button>

                  {reservations.length === 0 && dayCount > 0 && (
                    <p className="mt-3 rounded-lg border border-white/10 bg-black/20 p-2 text-[11px] text-muted-foreground">
                      {dayCount} booking{dayCount === 1 ? "" : "s"} this day, none in{" "}
                      {fromTime}–{toTime}.
                    </p>
                  )}


                  {isOpen && (
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                        <span>{isToday ? "Today's bookings" : "Bookings this day"}</span>
                        <span>{reservations.length}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {reservations.map((r) => {
                          const guest = Array.isArray(r.guests) ? r.guests[0] : r.guests;
                          const occasion = r.notes?.trim() || guest?.notes?.trim() || null;
                          return (
                            <li key={r.id}>
                              <button
                                type="button"
                                onClick={() => setDetailId(r.id)}
                                className="w-full rounded-lg border border-white/10 bg-black/30 p-2 text-left text-xs text-foreground/90 transition hover:border-white/30 hover:bg-black/50"
                              >
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className="font-medium">
                                    {fmtTime(r.starts_at)}–
                                    {fmtTime(
                                      new Date(
                                        new Date(r.starts_at).getTime() +
                                          (r.duration_minutes ?? 0) * 60000,
                                      ).toISOString(),
                                    )}{" "}
                                    · {guest?.full_name ?? "Guest"}
                                  </span>
                                  <span className="text-[10px] uppercase text-muted-foreground">
                                    {r.status.replace(/_/g, " ")}
                                  </span>
                                </div>
                                <div className="mt-0.5 text-[11px] text-muted-foreground">
                                  {r.party_size} {r.party_size === 1 ? "guest" : "guests"} ·{" "}
                                  {r.duration_minutes}m ·{" "}
                                  {(r.booking_type ?? "").replace(/_/g, " ")}
                                </div>
                                {guest?.phone && (
                                  <div className="text-[11px] text-muted-foreground">
                                    {guest.phone}
                                  </div>
                                )}
                                {r.preferred_area && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Area: {r.preferred_area}
                                  </div>
                                )}
                                {occasion && (
                                  <div className="mt-1 rounded-md border border-amber-400/30 bg-amber-400/10 px-1.5 py-1 text-[11px] text-amber-200">
                                    Occasion / notes: {occasion}
                                  </div>
                                )}
                                {r.deposit_required && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Deposit €{((r.deposit_amount_cents ?? 0) / 100).toFixed(2)}
                                  </div>
                                )}
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                  Ref {r.reference}
                                  {(r as { created_at?: string }).created_at && (
                                    <> · taken {new Date((r as { created_at: string }).created_at).toLocaleDateString("en-IE", { timeZone: "Europe/Dublin", day: "2-digit", month: "short" })}</>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}



                  <div className="mt-3 grid grid-cols-2 gap-1.5">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => requestChange(t, s)}
                        disabled={mut.isPending || status === s}
                        className={`rounded-md border px-2 py-1.5 text-[11px] font-medium transition ${
                          status === s
                            ? "border-white/30 bg-white/10 opacity-60"
                            : "border-white/10 bg-black/40 hover:bg-black/60"
                        }`}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[s]}`} />
                          {STATUS_LABEL[s]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        );
      })}


      {grouped.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card/30 p-8 text-sm text-muted-foreground">
          No tables configured yet.
        </div>
      )}

      <Dialog
        open={!!confirmState}
        onOpenChange={(o) => {
          if (!o) setConfirmState(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change table status</DialogTitle>
            <DialogDescription>
              {confirmState && (
                <>
                  <span className="font-medium text-foreground">{confirmState.label}</span>{" "}
                  · {STATUS_LABEL[confirmState.from]} →{" "}
                  <span className="font-medium text-foreground">
                    {STATUS_LABEL[confirmState.to]}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Reason (optional — logged in audit)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Guest checked in, spill cleanup, walk-in seated…"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmState(null)}>
              Cancel
            </Button>
            <Button onClick={confirmChange} disabled={mut.isPending}>
              Confirm update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Booking details</DialogTitle>
            <DialogDescription>
              {detail ? `Ref ${detail.reference}` : "Loading booking…"}
            </DialogDescription>
          </DialogHeader>

          {detailLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

          {detail && (() => {
            const guest = Array.isArray(detail.guests) ? detail.guests[0] : detail.guests;
            const tbl = Array.isArray(detail.tables) ? detail.tables[0] : detail.tables;
            const zone = tbl ? (Array.isArray(tbl.zones) ? tbl.zones[0] : tbl.zones) : null;
            const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
              <div className="flex justify-between gap-4 border-b border-border/40 py-1.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="text-right font-medium">{value ?? "—"}</span>
              </div>
            );
            return (
              <div className="space-y-5">
                <div>
                  <h3 className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Reservation
                  </h3>
                  <Row label="Status" value={detail.status.replace(/_/g, " ")} />
                  <Row
                    label="When"
                    value={new Date(detail.starts_at).toLocaleString("en-IE", {
                      timeZone: "Europe/Dublin",
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  />
                  <Row label="Duration" value={`${detail.duration_minutes} min`} />
                  <Row label="Party size" value={detail.party_size} />
                  <Row label="Type" value={detail.booking_type.replace(/_/g, " ")} />
                  <Row
                    label="Booked via"
                    value={
                      <span className="inline-flex flex-col items-end gap-1">
                        <ReservationSourceBadge source={(detail as { source?: string }).source} />
                        {bookedByName(detail) && (
                          <span className="text-xs font-normal text-muted-foreground">
                            by {bookedByName(detail)}
                          </span>
                        )}
                      </span>
                    }
                  />
                  <Row
                    label={detailTableIds.length > 1 ? "Tables" : "Table"}
                    value={
                      detailTableIds.length
                        ? detailTableIds
                            .map((id) => tables.find((t) => t.id === id)?.label ?? "—")
                            .join(" + ")
                        : tbl
                          ? `${tbl.label}${zone ? ` · ${zone.name}` : ""}`
                          : "Not assigned"
                    }
                  />
                  <div className="mt-2 space-y-1.5">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      Tables for this booking (tick several for big groups)
                    </p>
                    <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border/60 bg-muted/10 p-2">
                      {tables.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={detailTableIds.includes(t.id)}
                            disabled={tablesMut.isPending}
                            onChange={(e) =>
                              tablesMut.mutate({
                                id: detail.id,
                                status: detail.status,
                                table_ids: e.target.checked
                                  ? [...detailTableIds, t.id]
                                  : detailTableIds.filter((id) => id !== t.id),
                              })
                            }
                          />
                          {t.label} (cap {t.capacity})
                        </label>
                      ))}
                    </div>
                  </div>
                  <Row label="Preferred area" value={detail.preferred_area} />
                  <Row
                    label="Deposit"
                    value={
                      detail.deposit_required
                        ? `€${(detail.deposit_amount_cents / 100).toFixed(2)}`
                        : "Not required"
                    }
                  />
                  <div className="mt-3">
                    <ReservationNotes reservationId={detail.id} initialNotes={detail.notes} />
                  </div>
                </div>

                <div>
                  <h3 className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Customer
                  </h3>
                  <Row label="Name" value={guest?.full_name} />
                  <Row
                    label="Phone"
                    value={
                      guest?.phone ? (
                        <a className="underline" href={`tel:${guest.phone}`}>
                          {guest.phone}
                        </a>
                      ) : null
                    }
                  />
                  <Row
                    label="Email"
                    value={
                      guest?.email ? (
                        <a className="underline" href={`mailto:${guest.email}`}>
                          {guest.email}
                        </a>
                      ) : null
                    }
                  />
                  <Row label="Guest status" value={guest?.status} />
                  <Row label="Marketing opt-in" value={guest?.marketing_consent ? "Yes" : "No"} />
                  {guest?.notes && (
                    <p className="mt-2 rounded-lg border border-border/60 bg-muted/20 p-2 text-xs">
                      <span className="text-muted-foreground">Guest notes: </span>
                      {guest.notes}
                    </p>
                  )}
                </div>

                {detail.guestHistory.length > 1 && (
                  <div>
                    <h3 className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                      Booking history
                    </h3>
                    <ul className="space-y-1">
                      {detail.guestHistory.map((h) => (
                        <li
                          key={h.id}
                          className="flex justify-between rounded-md bg-muted/20 px-2 py-1 text-xs"
                        >
                          <span>
                            {new Date(h.starts_at).toLocaleDateString("en-IE", {
                              timeZone: "Europe/Dublin",
                            })}{" "}
                            · party {h.party_size}
                          </span>
                          <span className="text-muted-foreground">
                            {h.status.replace(/_/g, " ")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            {detail && (
              <Button
                onClick={() => {
                  const g = Array.isArray(detail.guests) ? detail.guests[0] : detail.guests;
                  setEditing({
                    id: detail.id,
                    reference: detail.reference,
                    booking_type: detail.booking_type,
                    starts_at: detail.starts_at,
                    duration_minutes: detail.duration_minutes,
                    party_size: detail.party_size,
                    notes: detail.notes,
                    preferred_area: detail.preferred_area,
                    guest_full_name: g?.full_name ?? null,
                    guest_phone: g?.phone ?? null,
                    guest_email: g?.email ?? null,
                  });
                  setDetailId(null);
                }}
              >
                Edit booking
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDetailId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

